import React from 'react';
import PageBanner from "@/components/pageBanner";

const TrainingFundamentalAnalysis = () => {
    return (
        <>
            <PageBanner title="Fundamental Analysis" slug="training-fundamental-analysis"/>
            <section className="generic-container py-14">

                <p>
                    Fundamental analysis seeks to determine the development of market trends by examining key economic
                    and political factors. Its primary objective is to establish a fair value of a currency that most
                    accurately reflects the overall market situation.
                </p>

                <p className={`mt-4`}>
                    This method is particularly suitable for long-term trading strategies, as it does not effectively
                    pinpoint optimal entry points in short-term trades. However, analyzing fundamental economic
                    indicators can help identify long-term trends at their early stages.
                </p>

                <p className={`font-bold text-lt-blue mt-8 text-2xl`}>Key Factors Considered in Fundamental
                    Analysis:</p>
                <ul className="list-disc ml-8 mt-2">
                    <li>Economic indicators</li>
                    <li>Monetary policies of central banks</li>
                    <li>Speeches by central bank officials, IMF experts, and government ministers</li>
                    <li>Political events affecting national economies</li>
                    <li>Environmental disasters and natural calamities</li>
                </ul>

                <p className={`mt-4`}>
                    Economic indicators reflecting the global or national economy and its specific sectors can be
                    categorized into several major groups:
                </p>
                <ol className="list-decimal ml-8 mt-2">
                    <li>Economic Growth Indicators</li>
                    <li>Leading Economic Indicators</li>
                    <li>Consumer Activity Indicators</li>
                </ol>
                <p className={`mt-4`}>
                    National economic research institutions and international organizations publish economic indicator
                    reports on a monthly, quarterly, or annual basis.
                </p>

                <p className={`font-bold text-lt-blue mt-8 text-2xl`}>Major Economic Growth Indicators:</p>
                <ul className="list-disc ml-8 mt-2">
                    <li>Gross National Product (GNP)</li>
                    <li>Gross Domestic Product (GDP)</li>
                    <li>Industrial Production Index</li>
                    <li>Economic Activity Index</li>
                </ul>
                <p className={`mt-4`}>
                    GDP serves as a measure of a country's overall economic performance, while GNP also includes income
                    generated from international economic activities. Simply put:
                </p>
                <ul className="list-disc ml-8 mt-2">
                    <li>The currency's value tends to appreciate with an increase in GDP/GNP.</li>
                    <li>The currency's value tends to depreciate when GDP/GNP declines.</li>
                </ul>
                <p className={`mt-4`}>
                    Forecasts of these indicators are available before their official release. Trading based on economic
                    events is possible, but the market impact is significant only when there is a substantial deviation
                    from expected values.
                </p>

                <p className={`font-bold text-lt-blue mt-8 text-2xl`}>Leading Economic Indicators:</p>
                <p className={`mt-4`}>
                    These indicators provide insights into overall economic prospects. Their growth generally leads to
                    an appreciation of the national currency. Important leading indicators include:
                </p>
                <ul className="list-disc ml-8 mt-2">
                    <li>Jobless Claims (Unemployment Rate)</li>
                    <li>Commodity Price Changes Index</li>
                    <li>Consumer Confidence Index</li>
                </ul>
                <p className={`mt-4`}>
                    Inflation indicators also play a crucial role in currency valuation. These include:
                </p>
                <ul className="list-disc ml-8 mt-2">
                    <li>Consumer Price Index (CPI)</li>
                    <li>Producer Price Index (PPI)</li>
                    <li>Retail Sales Figures</li>
                </ul>

                <p className={`font-bold text-lt-blue mt-8 text-2xl`}>Key Consumer Activity Indicators:</p>
                <ul className="list-disc ml-8 mt-2">
                    <li>Consumer Credit Volume</li>
                    <li>Personal Income & Expenditure Levels</li>
                </ul>
                <p className={`mt-4`}>
                    Economic activity indicators are published by leading research institutions and national economic
                    analysis offices.
                </p>

                <p className={`font-bold text-lt-blue mt-8 text-2xl`}>Cyclical Nature of Fundamental Factors:</p>
                <p className={`mt-4`}>
                    Fundamental factors exhibit two primary cycles:
                </p>
                <ol className="list-decimal ml-8 mt-2">
                    <li>Short-term cycle – Includes daily economic events that influence the market over a few trading
                        sessions, causing short-term fluctuations in currency pairs.
                    </li>
                    <li>Long-term cycle – Shaped by major macroeconomic indicators and political developments, which can
                        be anticipated or unexpected.
                    </li>
                </ol>
                <p className={`mt-4`}>
                    The impact of macroeconomic data on the market depends on pre-release expectations. Generally, the greater the divergence between forecasted and actual data, the stronger the market reaction. For instance, if the market expects a central bank to raise interest rates and it does, the reaction may be muted as the event was already "priced in." However, unexpected decisions or data releases can lead to significant market volatility.
                </p>

            </section>
        </>
    );
};

export default TrainingFundamentalAnalysis;